import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import type { Block, Inline } from '@contentful/rich-text-types';
import {
  type ImageSources,
  type VideoProps,
  CarouselCardItem,
} from '@snapchat/snap-design-system-marketing';

import { useContentfulImages } from '../../../../contentful-client/src';
import { useParseMediaContainer } from '../../hooks/useParseMediaContainer';
import { combineImageSources } from '../../utils/combineImageSources';
import { getImageSourceSettings } from '../Image';
import type { ImmersiveScrollBlockTileProps } from './types';

export const ImmersiveScrollBlockCard = (tile: ImmersiveScrollBlockTileProps) => {
  const { getImageSources } = useContentfulImages();

  const {
    media: { imageSource, videoSource, contentType },
    mobileMedia: {
      imageSource: mobileImageSource,
      videoSource: mobileVideoSource,
      contentType: mobileContentType,
    },
    thumbnailSource,
  } = useParseMediaContainer(tile.media);

  let imgSrcs: ImageSources | undefined;
  let videoProps: VideoProps | undefined;

  if (tile.media?.__typename === 'Image') {
    const { desktopSettings, mobileSettings } = getImageSourceSettings({
      desktopHeight: tile.media.media?.height ?? 0,
      mobileHeight: tile.media.mobileMedia?.height ?? 0,
      enableHighDpi: tile.media.enableHighDpi,
      quality: tile.media.quality,
    });

    imgSrcs = combineImageSources({
      desktop: getImageSources(imageSource, desktopSettings),
      mobile: getImageSources(mobileImageSource, mobileSettings),
    });
  }

  if (tile.media?.__typename === 'Video') {
    videoProps = {
      source: videoSource ?? '',
      mobileSource: mobileVideoSource,
      sourceType: contentType,
      mobileSourceType: mobileContentType,
      posterSource: thumbnailSource,
    };
  }

  const tileSlug = tile.slugReference?.slug || '';
  const tileTitle = tile.title?.json ? tile.title.json : '';
  const titleBody = tile.label?.json ? tile.label?.json : '';

  return (
    <CarouselCardItem
      key={`${tile.__typename}-${tile.sys.id}`}
      title={documentToPlainTextString(tileTitle as Block | Inline)}
      body={documentToPlainTextString(titleBody as Block | Inline)}
      url={tileSlug ? `/${tileSlug}` : undefined}
      imgSrcs={imgSrcs}
      imgAltText={tile.media?.media?.description}
      videoSource={videoProps?.source}
      mobileVideoSource={videoProps?.mobileSource}
      posterSource={videoProps?.posterSource}
      aspectRatio="9:16"
      shouldLoad
    />
  );
};
